'use client';

import { useEffect, useRef, useState } from 'react';

import { getExpandedRowModel } from '@tanstack/react-table';

import type { GetRowId } from './rowIdentity';
import type { ColumnDef, ExpandedState, Row, Table, TableOptions } from '@tanstack/react-table';

/** Fields both expansion modes accept. */
interface RowExpansionSharedConfig<TRow> {
  /**
   * Gates which rows can expand. Defaults to: expandable when the row has
   * sub-rows, or always when `renderExpandedContent` is set.
   */
  getRowCanExpand?: (row: TRow) => boolean;
  /** Makes a click anywhere on a row toggle its expansion, on top of the chevron. */
  expandOnRowClick?: boolean;
}

/** Expansion mode where a row opens into full-width custom content. */
interface RowExpansionContentConfig<TRow> extends RowExpansionSharedConfig<TRow> {
  /** Full-width content rendered in its own row beneath the parent row. */
  renderExpandedContent: (row: TRow) => React.ReactNode;
  getSubRows?: never;
  childColumns?: never;
  onChildRowClick?: never;
  childRowHref?: never;
}

/** Expansion mode where a row opens into typed child sub-rows. */
interface RowExpansionSubRowsConfig<TRow, TChildRow> extends RowExpansionSharedConfig<TRow> {
  /** Parent/child sub-rows. Child cells render from `childColumns`, not hand-written cells. */
  getSubRows: (row: TRow) => TChildRow[] | undefined;
  /**
   * Columns the child rows render from. A child row is laid out inside the
   * parent's grid, so it must declare at most as many columns as the parent
   * table (`childColumns.length <= columns.length`); the remaining width is
   * filled with an empty cell.
   */
  childColumns: ColumnDef<TChildRow, unknown>[];
  /**
   * A child-row click runs this, mirroring the parent table's `onRowClick`. A
   * child column whose cells hold their own controls opts its cell out with
   * `meta.ownsCellClicks`, the same as a parent action column.
   */
  onChildRowClick?: (childRow: TChildRow, parentRow: TRow) => void;
  /** Wraps each child-row cell in a link, mirroring the parent table's `rowHref`. */
  childRowHref?: (childRow: TChildRow, parentRow: TRow) => string;
  renderExpandedContent?: never;
}

/**
 * Enables native row expansion on a table. A row expands either into full-width
 * custom content or into typed child sub-rows; the two modes are mutually
 * exclusive, since the renderer can only draw one of them.
 */
export type RowExpansionConfig<TRow, TChildRow = never> =
  | RowExpansionContentConfig<TRow>
  | RowExpansionSubRowsConfig<TRow, TChildRow>;

/** `RowExpansionConfig` plus the row identity the table resolves for every row-keyed feature. */
export type ResolvedRowExpansionConfig<TRow, TChildRow = never> = RowExpansionConfig<TRow, TChildRow> & {
  getRowId: GetRowId<TRow>;
};

type ExpansionTableOptions<TRow> = Pick<
  TableOptions<TRow>,
  'getExpandedRowModel' | 'getRowCanExpand' | 'onExpandedChange'
>;

export interface UseRowExpansionResult<TRow> {
  /** Whether expansion is enabled. */
  enabled: boolean;
  /** Current expansion state (controlled). */
  expandedState: ExpandedState;
  /**
   * TanStack options to spread into `useReactTable`: everything except `state`,
   * which the caller merges with any other controlled state (e.g. sorting).
   * Empty when expansion is disabled.
   */
  tableOptions: ExpansionTableOptions<TRow>;
  /**
   * Expands a collapsed row, collapses an expanded one, on the table these
   * options were spread into. Callers bind the table once and expose the
   * remaining `(row) => void`.
   */
  toggleRow: (table: Table<TRow>, row: TRow) => void;
  /** Collapses every expanded row. */
  collapseAll: () => void;
}

/**
 * Consolidates the row-expansion state wiring shared by every expandable table:
 * controlled expanded state, the `useReactTable` option spread (expanded row
 * model, `getRowCanExpand`, `onExpandedChange`), and the
 * reset-on-change effect. Callers prepend the chevron column themselves via
 * `createExpandColumn` (the column must live at module scope for the suspense
 * wrapper's dummy header row).
 *
 * Expanded state is keyed by the table's row id, which is what lets it survive a
 * refetch that only changes fields on rows already on screen.
 *
 * `resetKey` should change whenever the visible row set is replaced (e.g. a
 * page/filter change) so a stale expansion can't survive off-screen. Pass a
 * stable value to keep rows expanded across renders (e.g. accumulated pages in
 * infinite scroll).
 */
export function useRowExpansion<TRow, TChildRow = never>(
  config: ResolvedRowExpansionConfig<TRow, TChildRow> | undefined,
  resetKey: unknown,
): UseRowExpansionResult<TRow> {
  const [expandedState, setExpandedState] = useState<ExpandedState>({});

  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (lastResetKey.current === resetKey) return;
    lastResetKey.current = resetKey;
    if (config) setExpandedState({});
  }, [resetKey]);

  const collapseAll = () => setExpandedState({});

  // Going through the row keeps TanStack's own semantics for a state of `true`
  // (an expand-all control sets it): it expands that into a per-row record
  // before flipping the one row.
  const toggleRow = (table: Table<TRow>, row: TRow) => {
    if (!config) return;
    table.getCoreRowModel().rowsById[config.getRowId(row)]?.toggleExpanded();
  };

  const tableOptions: ExpansionTableOptions<TRow> = config
    ? {
        getExpandedRowModel: getExpandedRowModel(),
        onExpandedChange: setExpandedState,
        getRowCanExpand: resolveGetRowCanExpand(config),
      }
    : {};

  return {
    enabled: !!config,
    expandedState,
    tableOptions,
    toggleRow,
    collapseAll,
  };
}

/**
 * Reset key for a table that holds its rows in memory: it changes when rows are added,
 * removed, or reordered, but not when a field on a row already on screen changes.
 */
export function rowIdResetKey<TRow>(data: TRow[], getRowId: GetRowId<TRow>): string {
  return JSON.stringify(data.map(getRowId));
}

/**
 * Child rows live outside TanStack's row model (they have their own shape and
 * their own columns), so its built-in `getCanExpand` never sees them and would
 * report every row non-expandable. Can-expand is answered from the config
 * instead: the caller's own predicate, always for full-width content, and
 * "has children" for sub-rows.
 */
function resolveGetRowCanExpand<TRow, TChildRow>(
  config: ResolvedRowExpansionConfig<TRow, TChildRow>,
): (row: Row<TRow>) => boolean {
  const { getRowCanExpand, renderExpandedContent, getSubRows } = config;

  if (getRowCanExpand) return (row) => getRowCanExpand(row.original);
  if (renderExpandedContent) return () => true;
  return (row) => !!getSubRows?.(row.original)?.length;
}
